import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useContext } from "react";
import { InternalLink } from "../components/utils/InternalLink";
import { ClaimsBanner } from "../components/view/ClaimsBanner";
import { HomeProject, ResponsiveBreakpoint } from "../components/view/HomeProject";
import { routes } from "../constants/routes";
import { ContentfulContext } from "../context/ContentfulContext";
import { Home, MarketingClaim, Project } from "../models/schema";
import { colors } from "../theme/colors";
import { generateId } from "../utils/utilities";

const HomePage = () => {
  const { content } = useContext(ContentfulContext);
  const home = content?.home as Home;
  const projects = home?.projectsCollection?.items as Project[];

  const getZindex = (position: number) => {
    return 100 - position * 1;
  };

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "100% 0% 100% 0% / 0% 80% 5% 100%",
          overflow: "hidden",
          position: "relative",
          mb: -10,
          maxHeight: { xs: 600, md: 900 },
          zIndex: 200
        }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            pt: 20,
            pb: { xs: 10, md: 20 },
            px: { xs: 10, md: 20 }
          }}>
          <Box sx={{ mt: 0 }}>
            <Typography
              textAlign="center"
              variant="h3"
              sx={{ display: { xs: "none", lg: "block" } }}
              mb={5}
              color={colors.white}>
              {home.titleText}
            </Typography>
            <Typography
              textAlign="center"
              variant="h4"
              sx={{ display: { xs: "none", md: "block", lg: "none" } }}
              mb={5}
              color={colors.white}>
              {home.titleText}
            </Typography>
            <Typography
              variant="h5"
              sx={{ display: { xs: "block", md: "none" }, whiteSpace: "nowrap" }}
              mb={5}
              textAlign="center"
              color={colors.white}>
              {home.titleText}
            </Typography>
            <Box
              component="img"
              src={home?.titleImage?.url as string}
              sx={{
                width: { xs: 300, md: 450, lg: 600 }
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 50, md: 100 },
            right: { xs: 20, md: 50 },
            fontSize: { xs: 16, md: 20 },
            fontWeight: 600,
            zIndex: 1
          }}>
          <InternalLink
            to={routes.solutions}
            sx={{
              color: colors.yellow
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box>{home.projectsAction}</Box>
              <ArrowForwardIosIcon sx={{ fontSize: { xs: 14, md: 18 } }} />
            </Box>
          </InternalLink>
        </Box>

        {isMobile ? (
          <Box
            component="video"
            loop
            autoPlay
            muted
            playsInline
            src={home?.promoVideoMobile?.url as string}
            sx={{
              height: "100%",
              width: "auto",
              filter: "brightness(0.7)"
            }}
          />
        ) : (
          <Box
            component="video"
            loop
            autoPlay
            muted
            playsInline
            src={home?.promoVideoDesktop?.url as string}
            sx={{
              height: "50%",
              width: "100%",
              filter: "brightness(0.7)"
            }}
          />
        )}
      </Box>

      {home.claimsBannerPosition === 0 && (
        <ClaimsBanner claims={home.claimsBannerCollection?.items} zIndex={getZindex(0)} />
      )}

      {projects
        ?.sort((a, b) =>
          // Sort by position if both present, otherwise sort by presence of position
          a.position && b.position ? a.position - b.position : a.position ? 1 : b.position ? -1 : 0
        )
        .map((project, index) => {
          const Project = () => (
            <HomeProject
              id={project.id as string}
              title={project.title as string}
              titlePrefix={home.project as string}
              marketingClaims={project.marketingClaimsCollection?.items as MarketingClaim[]}
              orientation={project.reverseLayout ? "right" : "left"}
              colorInvert={project.colorInvert as ResponsiveBreakpoint[]}
              zIndex={getZindex(project.position as number)}
              actionLabel={project.callToAction as string}
              backgroundUrlXS={project.marketingImageXs?.url as string}
              backgroundUrlSM={project.marketingImageSm?.url as string}
              backgroundUrlMD={project.marketingImageMd?.url as string}
              backgroundUrlLG={project.marketingImageLg?.url as string}
              backgroundUrlXL={project.marketingImageXl?.url as string}
            />
          );

          if (home.claimsBannerPosition && index === home.claimsBannerPosition - 1) {
            return (
              <Box key={generateId(5)}>
                <ClaimsBanner
                  claims={home.claimsBannerCollection?.items}
                  zIndex={getZindex(home.claimsBannerPosition as number)}
                />
                <Project />
              </Box>
            );
          }

          return <Project key={generateId(5)} />;
        })}

      {!isNaN(home.claimsBannerPosition as number) &&
        (home.claimsBannerPosition as number) > projects.length && (
          <ClaimsBanner claims={home.claimsBannerCollection?.items} zIndex={1} />
        )}
    </>
  );
};
export default HomePage;
